"use client";

/**
 * Global Error Page (Critical Errors)
 * 
 * แสดงเมื่อเกิดข้อผิดพลาดร้ายแรงระดับ root layout
 * หน้านี้จะแทนที่ root layout ทั้งหมด จึงต้องมี <html> และ <body> เอง
 * 
 * TOR Reference: Error Handling - ข้อความอธิบายชัดเจนเมื่อเกิดข้อผิดพลาด
 */

import { useEffect } from "react";

interface GlobalErrorProps {
    error: Error & { digest?: string };
    reset: () => void;
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
    useEffect(() => {
        // Log critical error
        console.error("Critical Error:", error);
    }, [error]);

    return (
        <html lang="th">
            <body style={{ margin: 0, fontFamily: "system-ui, sans-serif" }}>
                <div
                    style={{
                        minHeight: "100vh",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "2rem",
                        background: "linear-gradient(to bottom, #fef2f2, #ffffff)",
                        textAlign: "center",
                    }}
                >
                    {/* Error Icon */}
                    <div
                        style={{
                            width: "96px",
                            height: "96px",
                            borderRadius: "50%",
                            backgroundColor: "#fee2e2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "1.5rem",
                        }}
                    >
                        <svg
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#dc2626"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                    </div>

                    {/* Error Message */}
                    <h1
                        style={{
                            fontSize: "1.5rem",
                            fontWeight: 600,
                            color: "#1e3a5f",
                            marginBottom: "0.75rem",
                        }}
                    >
                        ระบบเกิดข้อผิดพลาดร้ายแรง
                    </h1>

                    <p
                        style={{
                            color: "#64748b",
                            maxWidth: "400px",
                            lineHeight: 1.6,
                            marginBottom: "2rem",
                        }}
                    >
                        ขออภัยครับ/ค่ะ ระบบไม่สามารถทำงานได้ในขณะนี้
                        กรุณารีเฟรชหน้าหรือลองใหม่อีกครั้งในภายหลัง
                    </p>

                    {/* Solutions Box */}
                    <div
                        style={{
                            backgroundColor: "#fef3c7",
                            border: "1px solid #fcd34d",
                            borderRadius: "8px",
                            padding: "1rem",
                            maxWidth: "400px",
                            marginBottom: "2rem",
                            textAlign: "left",
                        }}
                    >
                        <p
                            style={{
                                fontWeight: 600,
                                color: "#92400e",
                                marginBottom: "0.5rem",
                                fontSize: "0.875rem",
                            }}
                        >
                            แนวทางแก้ไข:
                        </p>
                        <ul
                            style={{
                                margin: 0,
                                paddingLeft: "1.25rem",
                                color: "#a16207",
                                fontSize: "0.875rem",
                            }}
                        >
                            <li style={{ marginBottom: "0.25rem" }}>กด &quot;ลองใหม่อีกครั้ง&quot; ด้านล่าง</li>
                            <li style={{ marginBottom: "0.25rem" }}>รีเฟรชหน้าเว็บ (F5 หรือ Ctrl+R)</li>
                            <li style={{ marginBottom: "0.25rem" }}>ปิดแท็บและเปิดใหม่</li>
                            <li>ติดต่อ 1164 หากยังพบปัญหา</li>
                        </ul>
                    </div>

                    {/* Buttons */}
                    <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", justifyContent: "center" }}>
                        <button
                            onClick={reset}
                            style={{
                                backgroundColor: "#1e3a5f",
                                color: "white",
                                border: "none",
                                padding: "0.75rem 1.5rem",
                                borderRadius: "6px",
                                fontSize: "1rem",
                                fontWeight: 500,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                            }}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                <path d="M3 3v5h5" />
                                <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                                <path d="M16 21h5v-5" />
                            </svg>
                            ลองใหม่อีกครั้ง
                        </button>
                        <a
                            href="/"
                            style={{
                                backgroundColor: "white",
                                color: "#1e3a5f",
                                border: "1px solid #e2e8f0",
                                padding: "0.75rem 1.5rem",
                                borderRadius: "6px",
                                fontSize: "1rem",
                                fontWeight: 500,
                                cursor: "pointer",
                                textDecoration: "none",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                            }}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                <polyline points="9,22 9,12 15,12 15,22" />
                            </svg>
                            กลับหน้าหลัก
                        </a>
                    </div>

                    {/* Help Text */}
                    <p
                        style={{
                            marginTop: "2rem",
                            fontSize: "0.875rem",
                            color: "#64748b",
                        }}
                    >
                        ต้องการความช่วยเหลือ?{" "}
                        <a href="tel:1164" style={{ color: "#1e3a5f", fontWeight: 500 }}>
                            โทร 1164
                        </a>
                    </p>
                </div>
            </body>
        </html>
    );
}
